import { Container, Title, Paper, Stack, Text, Card, Group, Badge, Button, Grid } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { IconFileText, IconGavel } from "@tabler/icons-react";
import { useVendorAuth } from "../auth/VendorAuthContext";
import { useTenderRegistrations } from "../hooks/useTenderRegistrations";
import { useVendorDocuments } from "../hooks/useVendorDocuments";
import dayjs from "dayjs";

const getVendorStatusLabel = (status: string | undefined): string => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "PENDING":
      return "Pending Approval";
    case "REGISTERED":
      return "Registered";
    case "SUSPENDED":
      return "Suspended";
    default:
      return status || "Unknown";
  }
};

export function VendorDashboard() {
  const navigate = useNavigate();
  const { vendor } = useVendorAuth();
  const { data: registrations, isLoading: loadingRegistrations } = useTenderRegistrations();
  const { data: documents } = useVendorDocuments();

  const activeRegistrations = registrations?.filter((r) => r.status === "REGISTERED") || [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Vendor Dashboard</Title>
          <Text c="dimmed" mt="xs">
            Welcome, {vendor?.businessName || vendor?.email || 'Vendor'}
          </Text>
        </div>

        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={500}>Account Status</Text>
                <Badge color={
                  vendor?.status === "ACTIVE" ? "green" 
                  : vendor?.status === "PENDING" ? "yellow"
                  : vendor?.status === "REGISTERED" ? "blue"
                  : "red"
                }>
                  {getVendorStatusLabel(vendor?.status)}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {vendor?.status === "ACTIVE"
                  ? "Your account is active. You can participate in tenders."
                  : vendor?.status === "PENDING"
                  ? "Your account is pending approval. Please wait for admin approval."
                  : vendor?.status === "REGISTERED"
                  ? "Please complete your business profile and upload all required documents to submit for approval."
                  : "Your account has been suspended. Please contact support."}
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={500}>Registered Tenders</Text>
                <IconGavel size={24} />
              </Group>
              <Text size="xl" fw={700}>
                {activeRegistrations.length}
              </Text>
              <Text size="sm" c="dimmed">
                Active registrations
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={500}>Documents</Text>
                <IconFileText size={24} />
              </Group>
              <Text size="xl" fw={700}>
                {documents?.length || 0}
              </Text>
              <Text size="sm" c="dimmed">
                Total documents uploaded
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={3}>Recent Tender Registrations</Title>
            <Button variant="light" color="green" onClick={() => navigate("/vendor/tenders")}>
              Browse Tenders
            </Button>
          </Group>

          {loadingRegistrations ? (
            <Text c="dimmed">Loading...</Text>
          ) : activeRegistrations.length === 0 ? (
            <Text c="dimmed">No tender registrations yet. Browse tenders to get started.</Text>
          ) : (
            <Stack gap="sm">
              {activeRegistrations.slice(0, 5).map((registration) => (
                <Card key={registration.id} shadow="xs" padding="sm" radius="md" withBorder>
                  <Group justify="space-between">
                    <div>
                      <Text fw={500}>{registration.tender?.referenceNo}</Text>
                      <Text size="sm" c="dimmed">
                        {registration.tender?.description}
                      </Text>
                      <Text size="xs" c="dimmed" mt="xs">
                        Registered: {dayjs(registration.registeredAt).format("MMM DD, YYYY")}
                      </Text>
                    </div>
                    <Group>
                      <Badge color="green">{registration.tender?.stage}</Badge>
                      <Button
                        size="xs"
                        variant="light"
                        color="green"
                        onClick={() => navigate(`/vendor/tenders/${registration.tenderId}`)}
                      >
                        View
                      </Button>
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Paper>

      </Stack>
    </Container>
  );
}

